/**
 * Décompte d'un lot attribué (lot 058) : fonctions pures de l'écran de saisie.
 *
 * Le serveur reste la référence (`backend/app/core/statement_lines.py`,
 * `services/lot_settlement_service.py`). L'aperçu pendant la saisie réutilise
 * les calculs de l'état de paiement du transport (`lib/transportStatements.js`) :
 * mêmes formules, mêmes arrondis (chaque ligne arrondie AVANT d'être sommée).
 * Dès qu'un brouillon est enregistré, l'écran affiche les montants du serveur.
 *
 * Ici, une ligne est un camion du lot (`lot_truck_id`) ; le prix par défaut
 * est celui du lot ; il n'y a ni dépense ni autre charge.
 *
 * Décompte flexible : le prix, le montant et la retenue d'une ligne ont une
 * valeur par défaut calculée et peuvent être saisis à la main
 * (`saisie.manuels[camion] = { prix, montant, retenue }`, en texte). Le serveur
 * reçoit `price_override`, `amount_override`, `retention_override` (`null`
 * pour revenir au calcul) et rend `*_default` / `*_override`.
 *
 * Aucun appel réseau, aucun composant : tout se teste seul.
 */
import { arrondiDevise } from './formats';
import {
  calculerLigne,
  manquantApercu,
  nombreSaisi,
  texteChamp,
} from './transportStatements';

/** Les valeurs d'une ligne qu'on peut saisir : clé de la saisie, nom du serveur. */
export const CHAMPS_SAISIS = [
  ['prix', 'price'],
  ['montant', 'amount'],
  ['retenue', 'retention'],
];

/** Les valeurs saisies à la main d'un décompte enregistré, en texte : `{ camion: { prix, montant, retenue } }`. */
export const manuelsDecompte = (decompte) => {
  const manuels = {};
  (decompte?.lines || []).forEach((ligne) => {
    const valeurs = {};
    CHAMPS_SAISIS.forEach(([cle, champ]) => {
      const saisie = ligne[`${champ}_override`];
      if (saisie !== null && saisie !== undefined) valeurs[cle] = texteChamp(saisie);
    });
    if (Object.keys(valeurs).length > 0) manuels[ligne.lot_truck_id] = valeurs;
  });
  return manuels;
};

/** Les camions affichés : ceux que le serveur propose, plus ceux du brouillon qu'il ne propose plus. */
export const lignesDecompte = (eligible, decompte) => {
  const proposees = eligible?.trucks || [];
  const connues = new Set(proposees.map((ligne) => ligne.lot_truck_id));
  const orphelines = (decompte?.lines || [])
    .filter((ligne) => !connues.has(ligne.lot_truck_id))
    .map((ligne) => ({
      lot_truck_id: ligne.lot_truck_id,
      trip_id: ligne.trip_id,
      tractor_plate: ligne.tractor_plate,
      tank_plate: ligne.tank_plate,
      loading_date: ligne.loading_date,
      loading_voucher_number: ligne.loading_voucher_number,
      trip_status: ligne.trip_status,
      quantity: ligne.quantity,
      quantity_estimated: ligne.quantity_estimated,
      price: ligne.price,
      shortage_known: ligne.shortage_known,
      shortage_liters: ligne.shortage_liters,
      delivered_volume: ligne.delivered_volume,
      tolerance_liters: ligne.tolerance_liters,
      already_retained: ligne.already_retained,
      warnings: ligne.warnings || [],
      selected: true,
      orpheline: true,
    }));
  return [...proposees, ...orphelines];
};

/**
 * L'état de saisie (tout en texte, comme les champs) depuis ce que renvoie le
 * serveur : le décompte enregistré (absent pour un nouveau) et les choix possibles.
 */
export const saisieDecompte = (decompte, eligible) => {
  const defauts = eligible?.defaults || {};
  const enregistres = new Set((decompte?.lines || []).map((ligne) => ligne.lot_truck_id));
  const avances = {};
  (eligible?.advances || []).forEach((avance) => {
    if (avance.selected_amount !== null && avance.selected_amount !== undefined) {
      avances[avance.payment_id] = texteChamp(avance.selected_amount);
    }
  });
  const manuels = manuelsDecompte(decompte);
  return {
    emisLe: decompte?.issued_at ? String(decompte.issued_at).slice(0, 10) : defauts.issued_at || '',
    prixRetenue: texteChamp(decompte ? decompte.retention_price : defauts.retention_price),
    notes: decompte?.notes || '',
    selectionnes: enregistres,
    livre: {},
    avances,
    manuels,
    // Ce que le serveur garde : une saisie effacée lui est renvoyée à `null`.
    manuelsEnregistres: manuels,
  };
};

/** Prix de retenue de l'aperçu : celui saisi, à défaut le prix du lot (comme le serveur). */
export const prixRetenueApercu = (saisie, prixLot) => {
  const saisi = nombreSaisi(saisie?.prixRetenue);
  if (saisi !== null) return saisi;
  return Number(prixLot) || 0;
};

/**
 * Un camion prêt pour l'affichage : les faits du serveur, la saisie et l'aperçu
 * des montants. Un brouillon propre (`enregistree`) montre les chiffres du serveur.
 */
export const ligneDecompteAffichee = (ligne, saisie, devise = 'XOF', prixLot = null, enregistree = null) => {
  const prixDuLot = ligne.price === null || ligne.price === undefined ? prixLot : ligne.price;
  const manuel = saisie.manuels?.[ligne.lot_truck_id] || {};
  const prixSaisi = nombreSaisi(manuel.prix);
  const montantSaisi = nombreSaisi(manuel.montant);
  const retenueSaisie = nombreSaisi(manuel.retenue);
  const manquant = manquantApercu({
    quantite: ligne.quantity,
    manquantConnu: ligne.shortage_known,
    manquant: ligne.shortage_liters,
    livre: saisie.livre[ligne.lot_truck_id],
  });
  // Le calcul par défaut, avec le prix saisi s'il y en a un (comme le serveur).
  const apercu = calculerLigne(
    {
      quantite: ligne.quantity,
      prix: prixSaisi ?? prixDuLot,
      manquant,
      tolerance: ligne.tolerance_liters,
      prixRetenue: prixRetenueApercu(saisie, prixLot),
      depense: 0,
      dejaRetenu: ligne.already_retained,
    },
    devise,
  );
  const montant = montantSaisi !== null ? arrondiDevise(montantSaisi, devise) : apercu.montant;
  const retenue = retenueSaisie !== null ? arrondiDevise(retenueSaisie, devise) : apercu.retenue;
  const chiffres = enregistree
    ? {
        prix: enregistree.price ?? prixSaisi ?? prixDuLot,
        prixDefaut: enregistree.price_default ?? prixDuLot,
        montant: enregistree.amount,
        montantDefaut: enregistree.amount_default ?? enregistree.amount,
        litresRetenus: enregistree.retained_liters,
        retenue: enregistree.retention,
        retenueDefaut: enregistree.retention_default ?? enregistree.retention,
        solde: enregistree.balance,
      }
    : {
        prix: prixSaisi ?? prixDuLot,
        prixDefaut: prixDuLot,
        montant,
        montantDefaut: apercu.montant,
        litresRetenus: apercu.litresRetenus,
        retenue,
        retenueDefaut: apercu.retenue,
        solde: arrondiDevise(montant - retenue, devise),
      };
  const saisis = { prix: prixSaisi !== null, montant: montantSaisi !== null, retenue: retenueSaisie !== null };
  return {
    ...ligne,
    // `quantite` : la clé que somme `calculerTotaux`.
    quantite: ligne.quantity,
    manquant: enregistree ? enregistree.shortage_liters : manquant,
    manquantInconnu: enregistree ? !enregistree.shortage_known : manquant === null,
    ...chiffres,
    saisis,
    manuel: saisis.prix || saisis.montant || saisis.retenue,
  };
};

/** Les avertissements du serveur qui ont une forme courte pour la pastille (le texte complet reste en infobulle). */
const ALERTES_COURTES = [
  [/^Volume chargé estimé/i, 'Volume estimé'],
  [/^Chargement absent du stock/i, 'Absent du stock'],
];

/**
 * Les pastilles d'un camion : ce qui reste à revoir, sans phrase. Le dépoté
 * inconnu ne fait pas de pastille : la case « Dépoté (L) » le dit déjà.
 */
export const pastillesLigne = (ligne) => {
  const pastilles = [];
  const ajouter = (texte, ton, titre) => {
    if (!pastilles.some((pastille) => pastille.texte === texte)) pastilles.push({ texte, ton, titre });
  };
  if (ligne.quantity_estimated) ajouter('Volume estimé', 'warning');
  if (ligne.orpheline) ajouter('Plus proposé', 'destructive');
  if (ligne.prix === null || ligne.prix === undefined) ajouter('Prix du lot absent', 'warning');
  (ligne.warnings || []).forEach((avertissement) => {
    if (/^Dépoté non constaté/i.test(avertissement)) return;
    const court = ALERTES_COURTES.find(([motif]) => motif.test(avertissement));
    ajouter(court ? court[1] : avertissement, 'warning', avertissement);
  });
  return pastilles;
};

/** Le corps de l'enregistrement d'un brouillon (création ou modification). */
export const corpsDecompte = ({ lignes, saisie, avances }) => {
  const choisies = lignes.filter((ligne) => saisie.selectionnes.has(ligne.lot_truck_id));
  const lignesCorps = [];
  choisies.forEach((ligne) => {
    const entree = { lot_truck_id: ligne.lot_truck_id };
    // Le dépoté ne s'écrit qu'une fois : jamais renvoyé pour un manquant déjà constaté.
    const livre = nombreSaisi(saisie.livre[ligne.lot_truck_id]);
    if (!ligne.shortage_known && livre !== null) entree.delivered_volume = livre;
    // Une valeur saisie part en nombre ; effacée alors que le serveur la garde, elle part à `null`.
    const manuel = saisie.manuels?.[ligne.lot_truck_id] || {};
    const enregistre = saisie.manuelsEnregistres?.[ligne.lot_truck_id] || {};
    CHAMPS_SAISIS.forEach(([cle, champ]) => {
      const valeur = nombreSaisi(manuel[cle]);
      if (valeur !== null) entree[`${champ}_override`] = valeur;
      else if (nombreSaisi(enregistre[cle]) !== null) entree[`${champ}_override`] = null;
    });
    if (Object.keys(entree).length > 1) lignesCorps.push(entree);
  });
  const corps = {
    line_ids: choisies.map((ligne) => ligne.lot_truck_id),
    issued_at: saisie.emisLe || undefined,
    lines: lignesCorps,
    advances: avances
      .filter((avance) => saisie.avances[avance.payment_id] !== undefined)
      .map((avance) => ({
        payment_id: avance.payment_id,
        amount: nombreSaisi(saisie.avances[avance.payment_id]) ?? 0,
      })),
    notes: saisie.notes.trim(),
  };
  const retenue = nombreSaisi(saisie.prixRetenue);
  if (retenue !== null) corps.retention_price = retenue;
  return corps;
};

/** Les chiffres d'une situation de paiement, dans l'ordre de leurs tuiles (clé du serveur, libellé). */
export const TUILES_SITUATION = [
  ['confirmed_value', 'Valeur confirmée'],
  ['settled_amount', 'Décompté'],
  ['retention_total', 'Retenues'],
  ['advances_paid', 'Acomptes versés'],
  ['paid_total', 'Payé'],
  ['remaining_due', 'Reste dû'],
  ['net_due', 'Net dû'],
];

/** Une situation est-elle vide (rien de confirmé, rien versé) ? */
export const situationVide = (situation) =>
  !situation ||
  (!Number(situation.confirmed_trucks) &&
    !Number(situation.advances_paid) &&
    !Number(situation.billed) &&
    !Number(situation.draft_count));
